import { useEffect, useState, type ReactNode } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, Avatar, getStatusBadge } from '@/backoffice/components/ui';
import { TrackingMap } from '@/backoffice/components/tracking/TrackingMap';
import { useOrderTracking } from '@/backoffice/hooks/useOrderTracking';
import { fetchClientOrders, type Order } from '@/domains/client/services/client.service';
import {
  MapPin,
  Clock,
  Phone,
  Navigation,
  Truck,
  Package,
  Check,
  Loader2,
  Wifi,
} from 'lucide-react';

export function LiveTracking() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    fetchClientOrders()
      .then((data) => {
        const trackable = data.filter((o) => o.statut === 'in_progress');
        setOrders(trackable);
        if (trackable.length > 0) {
          setSelectedId(trackable[0].id);
        }
      })
      .catch(() => setOrders([]))
      .finally(() => setLoadingOrders(false));
  }, []);

  const { tracking, loading, error, pickup, dropoff, driver, live } = useOrderTracking(selectedId);

  const driverUser = tracking?.order.driver;
  const driverName = driverUser
    ? `${driverUser.firstnames} ${driverUser.lastname}`
    : null;

  if (loadingOrders) {
    return (
      <DashboardLayout userType="client" title="Suivi en direct">
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      </DashboardLayout>
    );
  }

  if (orders.length === 0) {
    return (
      <DashboardLayout userType="client" title="Suivi en direct">
        <Card>
          <div className="text-center py-12">
            <Navigation className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune livraison en cours</h3>
            <p className="text-gray-500">
              Vos livraisons actives apparaîtront ici avec la position en temps réel du livreur.
            </p>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client" title="Suivi en direct">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <Card className="mb-6 bg-blue-50 border-blue-100">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-blue-800">
            <strong>Carte :</strong> les points de collecte et livraison sont positionnés selon la zone
            (Kaloum, Ratoma, Dixinn…), pas l'adresse exacte. Le GPS du livreur est mis à jour en temps réel.
          </p>
          {live && (
            <span className="text-xs text-emerald-700 flex items-center gap-1 shrink-0">
              <Wifi className="w-3.5 h-3.5" />
              Suivi en direct
            </span>
          )}
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="h-[500px] p-0 overflow-hidden relative">
            <TrackingMap pickup={pickup} dropoff={dropoff} driver={driver} />
            {tracking?.eta_minutes != null && (
              <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-600" />
                  <div>
                    <p className="text-sm text-gray-500">Arrivée estimée</p>
                    <p className="font-bold text-gray-900">{tracking.eta_minutes} min</p>
                    {tracking.routing_source === 'osrm' && (
                      <p className="text-xs text-blue-500">Itinéraire routier</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!driver && (
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 rounded-lg p-3 text-sm text-gray-600 z-[1000]">
                En attente de la position GPS du livreur...
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          {orders.length > 1 && (
            <Card>
              <p className="text-sm font-medium text-gray-700 mb-2">Commandes en cours</p>
              <div className="flex flex-wrap gap-2">
                {orders.map((o) => (
                  <Button
                    key={o.id}
                    size="sm"
                    variant={selectedId === o.id ? 'primary' : 'outline'}
                    onClick={() => setSelectedId(o.id)}
                  >
                    {o.code_suivi}
                  </Button>
                ))}
              </div>
            </Card>
          )}

          {tracking && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-500">Commande</p>
                  <p className="font-semibold text-gray-900">{tracking.order.code}</p>
                </div>
                {getStatusBadge(tracking.order.status)}
              </div>

              {driverName && driverUser && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                  <Avatar name={driverName} size="lg" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{driverName}</p>
                    <p className="text-sm text-gray-500">{driverUser.phone}</p>
                  </div>
                  <a
                    href={`tel:${driverUser.phone}`}
                    className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                </div>
              )}

              <div className="space-y-3">
                <TimelineStep
                  done
                  icon={<Check className="w-4 h-4 text-emerald-600" />}
                  title="Colis collecté"
                  subtitle={tracking.order.departure_zone?.name}
                />
                <div className="w-0.5 h-6 bg-blue-200 ml-4" />
                <TimelineStep
                  active={tracking.order.status === 'in_progress'}
                  icon={<Truck className="w-4 h-4 text-teal-600" />}
                  title="En cours de livraison"
                  subtitle={
                    tracking.distance_km != null
                      ? `${tracking.distance_km} km restants`
                      : 'En approche...'
                  }
                />
                <div className="w-0.5 h-6 bg-gray-200 ml-4" />
                <TimelineStep
                  icon={<Package className="w-4 h-4 text-gray-400" />}
                  title="Livraison"
                  subtitle={tracking.order.arrival_zone?.name}
                  muted
                />
              </div>

              {tracking.timeline.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Historique</p>
                  <div className="space-y-2">
                    {tracking.timeline.slice(0, 4).map((log) => (
                      <div key={log.id} className="text-sm text-gray-600">
                        <span className="font-medium text-gray-800">{log.note ?? log.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loading && (
                <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Mise à jour...
                </p>
              )}
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function TimelineStep({
  icon,
  title,
  subtitle,
  done,
  active,
  muted,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  done?: boolean;
  active?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        done ? 'bg-emerald-100' : active ? 'bg-blue-100 animate-pulse' : 'bg-gray-100'
      }`}>
        {icon}
      </div>
      <div className="flex-1">
        <p className={`font-medium ${muted ? 'text-gray-400' : 'text-gray-900'}`}>{title}</p>
        {subtitle && (
          <p className={`text-sm flex items-center gap-1 ${muted ? 'text-gray-400' : 'text-gray-500'}`}>
            <MapPin className="w-3 h-3" />
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
